import { NextPage, GetServerSideProps } from 'next';
import { get, pickBy } from 'lodash';
import { useRouter } from 'next/router';
import Head from 'next/head';
import { fetchArticle, fetchSearchUrl } from 'lib/AutolistAPI';
import Layout from 'components/Layout';
import Article from 'components/Article';
import { ArticleInterface } from 'types/Apis/ArticleInterface';
import StructuredDataComponent from 'components/StructuredData';
import StructuredData from 'lib/StructuredData/Article';
import Error from 'pages/_error';
import BackSubNav from 'components/BackSubNav';
import SeoSrpSubNav from 'components/SeoSrpSubNav';
import MultiAdapter from 'lib/GoogleAds/Adapters/Multi';
import AdUnit from 'lib/GoogleAds/AdUnit';
import DeviceScreen from 'lib/DeviceScreen';
import { CONTENT_URL } from 'lib/ContentGroups';
import useAdSetup from 'lib/hooks/useAdSetup';
import useCarGurusAdSetup from 'lib/hooks/useCarGurusAdSetup';
import CGPixelAd from 'components/Ad/CGPixelAd';
import { useContentGroups } from 'lib/hooks/useGoogleAnalytics';
import breadcrumbStructuredData from 'lib/StructuredData/breadcrumbStructuredData';
// eslint-disable-next-line import/no-unresolved
import { captureException } from 'sentry-alias';
import { SearchURL } from 'types/Apis/SearchURL';
import i18next from 'lib/i18next';
import config from 'lib/config';
import { AdInfo } from 'lib/CarGurusAds';

const parentPageMapping: Record<string, any> = {
  engineering: 'Engineering',
  comparisons: 'Guides',
  guides: 'Guides',
};

interface ArticlePageProps {
  article?: ArticleInterface;
  related?: ArticleInterface[];
  searchUrl?: SearchURL | null;
  statusCode?: number;
  carGurusAdInfo?: AdInfo | null;
}

const ArticlePage: NextPage<ArticlePageProps> = function ArticlePage({
  article,
  related,
  statusCode,
  searchUrl,
  carGurusAdInfo,
}: ArticlePageProps): JSX.Element {
  const adInfo = new MultiAdapter({
    searchUrl,
    article,
  });
  const { asPath } = useRouter();

  // HACK: temporary solution to add targeting to specific articles
  const parentPageTargeting: { content?: string } = {};

  if (get(article, 'parent_page.slug') === 'cpo') {
    parentPageTargeting.content = 'mercedescpo';
  }

  useContentGroups({ default: CONTENT_URL });

  const adUnit = new AdUnit(adInfo.adUnitAttributes);

  useAdSetup(adUnit.toString(), {
    pubtemplate: CONTENT_URL,
    adflow: adInfo.targeting.prod === 'review' ? 'native-1' : undefined,
    ...adInfo.targeting,
    ...pickBy(parentPageTargeting),
  });

  let carGurusAdTargeting: AdInfo = {
    ad_unit: '/19485787/AutoList.com/Research',
  };

  if (carGurusAdInfo) {
    carGurusAdTargeting = { ...carGurusAdInfo };
  }

  useCarGurusAdSetup(carGurusAdTargeting.ad_unit as string, {
    make: carGurusAdTargeting.make,
    model: carGurusAdTargeting.model,
    year: carGurusAdTargeting.year,
    pageType: carGurusAdTargeting.page_type,
    ageType: carGurusAdTargeting.age_type,
    cpo: carGurusAdTargeting.cpo,
    inLine: carGurusAdTargeting.in_line,
    bodyStyle: carGurusAdTargeting.body_style,
    category: carGurusAdTargeting.category,
    app: carGurusAdTargeting.app,
    spid: carGurusAdTargeting.spid,
  });

  if (!article || (statusCode && statusCode !== 200)) {
    return <Error statusCode={statusCode} />;
  }

  const shareUrl = new URL(`https://${config.host}`);
  if (asPath) shareUrl.pathname = asPath;

  const parentPage = article.parent_page;

  function parentPagePath(): string {
    return `/${parentPage.slug in parentPageMapping ? parentPage.slug : 'news-and-analysis'}`;
  }

  function parentPageTitle(): string {
    return parentPage?.slug in parentPageMapping ? parentPageMapping[parentPage.slug] : 'News';
  }

  const subNav = searchUrl ? (
    <SeoSrpSubNav article={article} searchUrl={searchUrl} />
  ) : (
    <BackSubNav url={parentPagePath()} title={parentPageTitle()} />
  );

  const structuredData = new StructuredData({
    ...article,
    ...{ shareUrl },
  });

  return (
    <>
      <CGPixelAd targeting={{}} />
      <Layout
        title={article.title}
        description={article.content_summary}
        indexed={article.indexed}
        subNav={subNav}
      >
        <Head>
          <meta
            content={article.hero_image || article.thumb_image}
            property="og:image"
            key="og:image"
          />
          <meta content="article" property="og:type" key="og:type" />
          <link rel="preload" href={article.thumb_image} as="image" key="article-hero" />
          <link rel="canonical" href={shareUrl.toString()} />
        </Head>
        <Article related={related} shareUrl={shareUrl.toString()} article={article} />
      </Layout>
      <StructuredDataComponent data={structuredData.toJSON()} />
      <StructuredDataComponent
        data={breadcrumbStructuredData({
          url: shareUrl.toString(),
          title: article.title,
          breadcrumbCategory: 'guides',
        })}
      />
      <style jsx global>{`
        body {
          font-smoothing: antialiased;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
        }
      `}</style>
    </>
  );
};

type QueryParams = {
  parentPage: string;
  slug: string;
};

export const getServerSideProps: GetServerSideProps<ArticlePageProps, QueryParams> = async ({
  req,
  res,
  query,
  locale = i18next.language,
}) => {
  const bypassCache = true;
  let fetchedArticle;

  try {
    fetchedArticle = await fetchArticle(
      String(query.slug),
      {
        parent_page: String(query.parentPage),
        related: true,
        locale,
      },
      { bypassCache }
    );
  } catch (error) {
    // When there is an api error, we don't want the page to 500 because we
    // want graceful error handling on Autolist-web, the api has its own error
    // monitoring
    res.statusCode = 500;
    captureException(error);
  }

  if (!fetchedArticle) {
    res.statusCode = 404;
  }

  const { article, related } = fetchedArticle || {};
  const jumpstartDeviceTypeParam = DeviceScreen.isMobile(req.headers) ? 'mobile' : 'desktop';

  let searchUrl;
  try {
    searchUrl = await fetchSearchUrl(String(query.parentPage), {
      jumpstart: jumpstartDeviceTypeParam,
      ads: 'web',
    });
  } catch (e) {
    captureException(e);
  }

  let carGurusAdInfo;
  if (searchUrl) {
    carGurusAdInfo = searchUrl.data.attributes.ad_info;
  }

  return {
    props: {
      article: article ?? null,
      related: related ?? null,
      searchUrl: searchUrl ?? null,
      statusCode: res.statusCode,
      carGurusAdInfo: carGurusAdInfo ?? null,
    },
  };
};

export default ArticlePage;
